// client/src/utils/fxDisplay.ts
//
// TT$<->US$ display conversion at the Settings-configured / quotation-pinned
// live rate (BUSINESS_RULES#usd-cost-and-ttd-display).
//
// CLIENT-ONLY, and deliberately not part of `utils/pricing.ts` next door:
// that file is MIRRORED against `src/utils/pricing.js`, and this rate has no
// server twin (`resolveVendorInvoiceCurrency` + the vendor-invoice cost
// engine own the SERVER-side TTD<->USD story on parsed invoices, a different
// question). Adding a half to one side of a mirror is exactly how the parity
// harness (`mirroredUtilParity.test.js`, "the twins export the same
// surface") earns its keep — a client-only export in a mirrored file is the
// dangerous direction there. The one exception is `usdToTtdExact` (D119.17):
// the server stores the same exact cost, so it lives in the pair and is re-exported here.
//
// 2026-09-22 one-owner audit (defect #4) — extracted out of
// `QuotationDetailPage.tsx`, where these two lived as private, unexported
// consts. `client/src/features/clientInvoice/lineCostCurrency.ts` needed a
// TTD<->USD converter and, per CLAUDE.md's "never copy it, extract it"
// (strangle, don't duplicate), that extraction happens here rather than a
// second private copy growing in ClientInvoicePage.tsx. QuotationDetailPage
// now imports these instead of defining them; behaviour is unchanged
// (`QuotationDetailPage*.test.tsx` and this file's own test both pin it).

import { ttdFromUsd } from './pricing';
import { ceilPrice } from './priceUtils';

/**
 * A cost somebody states in some currency, as the US$ figure it is applied
 * as — or null when it cannot be stated in US$ at all.
 *
 * `ttdToUsd` is US$ per TT$1 (e.g. 0.1667 when 6 TTD = 1 USD) — the
 * convention `getExchangeRate()`/`ttdUsdRate.service.js` shape their rate in,
 * NOT `fxRateAtPurchase`/`fxRateAtCost`'s TT$-per-US$1 convention; a caller
 * converting from one of those pins the rate (`1 / fxRateAtPurchase`) before
 * calling in.
 *
 * A TT$ price is ROUNDED to the cent, not ceiled; a US$ price passes through
 * unchanged. This is the display/refusal-aware figure (the client invoice's
 * cost currency uses it). A QUOTATION line does not store it: since D85.6 a
 * line stores {@link usdCostFacts} (cost ceiled once from the unrounded
 * conversion, the exact beside it), and the TT$ column shows the line's PIN
 * (`unitPriceTtd`), which is what stops the cost ratcheting on redisplay.
 *
 * Null, never a fallback, for three cases: no rate has loaded, the currency
 * is one this system holds no rate for (it holds exactly one, TTD<->USD), or
 * the source stated no currency at all. The caller must then refuse — a
 * guess here is indistinguishable on screen from a real US$ price. A price
 * of zero is NOT one of those cases: zero converts to zero.
 */
export const costToUsd = (
  price: unknown,
  currency: string | null | undefined,
  ttdToUsd: number | null,
): number | null => {
  const exact = costToUsdExact(price, currency, ttdToUsd);
  if (exact == null) return null;
  return isTtd(currency) ? Math.round(exact * 100) / 100 : exact;
};

const isTtd = (currency: string | null | undefined): boolean => String(currency || '').trim().toUpperCase() === 'TTD';

/**
 * D85.6 (2026-10-02): the UNROUNDED conversion — what a quotation line stores
 * as `unitPriceExact` (the line's cost `unitPrice` is `ceilPrice` of it, the
 * markup goes on it: BUSINESS_RULES#markup-cost-once). Same contract as
 * {@link costToUsd}, which is this rounded to the cent.
 */
export const costToUsdExact = (
  price: unknown,
  currency: string | null | undefined,
  ttdToUsd: number | null,
): number | null => {
  const amount = typeof price === 'number' ? price : parseFloat(String(price ?? ''));
  if (!Number.isFinite(amount)) return null;
  const code = String(currency || '').trim().toUpperCase();
  if (code === 'USD') return amount;
  if (code !== 'TTD') return null;
  if (!ttdToUsd || !Number.isFinite(ttdToUsd) || ttdToUsd <= 0) return null;
  return amount * ttdToUsd;
};

/**
 * D85.6: the cost a quotation line STORES for a stated price — `unitPrice`
 * (a TT$ price ceiled to the cent, the one round-up every line writer uses)
 * and `unitPriceExact` (the unrounded US$ the markup goes on). A US$ price is
 * stored as typed. Null exactly where {@link costToUsdExact} is null.
 */
export const usdCostFacts = (
  price: unknown,
  currency: string | null | undefined,
  ttdToUsd: number | null,
): { unitPrice: number; unitPriceExact: number } | null => {
  const exact = costToUsdExact(price, currency, ttdToUsd);
  if (exact == null) return null;
  return { unitPrice: isTtd(currency) ? ceilPrice(exact) : exact, unitPriceExact: exact };
};

/**
 * A US$ figure as the TT$ figure beside it, or null when there is no rate to
 * say it with.
 *
 * The counterpart of {@link costToUsd}: an absent rate is not a rate of 1.
 * `ceilPrice` on the way out (via {@link ttdFromUsd}). A quotation line that
 * was PRICED in TT$ shows its pin (`unitCostTtd`), not this, so a cost
 * ceiled in does not ratchet on redisplay.
 */
export const usdToTtd = (usd: number | null | undefined, ttdToUsd: number | null): number | null =>
  ttdFromUsd(usd, ttdToUsd);

/**
 * D119.10 / D119.17: the UNROUNDED TT$ for a US$ figure (a client-invoice line's cost). The owner is
 * `pricing.usdToTtdExact` (mirrored with the server, which stores the same exact cost); the counterpart of
 * {@link costToUsdExact}. {@link usdToTtd} is this ceiled, which a test pins.
 */
export { usdToTtdExact } from './pricing';
